// Time-zone helpers.
//
// Convention (Phase 4 — canonical):
//   • Match date/time and practice session date/time are stored as
//     PITTSBURGH WALL CLOCK (America/New_York) — HH:MM strings for
//     time, YYYY-MM-DD strings for date, with NO timezone info in the
//     DB.  A row saying `time = "14:30"` for `date = "2026-07-04"`
//     means 2:30 PM in Pittsburgh (EDT since it's July), which is
//     6:30 PM UTC on that day.
//   • Timestamps stamped by the DB (CreatedAt, UpdatedAt, PublishedAt,
//     PaidAt, etc.) are stored as UTC via SYSUTCDATETIME() and
//     rendered in ET at display time.
//
// This replaces the older Central-time-stored + "add 1 hour to display"
// scheme.  If you find existing rows that look 1 hour ahead of what
// they should be, they were entered under the OLD convention (Central
// Time) — edit the match/practice inline to correct.

// Use the IANA zone so DST is handled automatically. `Intl` looks this
// up in the ICU database bundled with modern Node / V8; every browser
// released since 2018 supports it.
const TZ = "America/New_York";

// ── Instant formatters (input: Date / ISO string / ms number) ─────────

// "3:45 PM" in Eastern Time. Handles DST automatically.
export function fmtTimeEastern(d) {
  if (d == null) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

// "Sat, Jul 4" in Eastern Time.
export function fmtDateEastern(d) {
  if (d == null) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    weekday: "short",
    month: "short",
    day: "numeric"
  }).format(date);
}

// "Sat, Jul 4, 2026 · 3:45 PM ET" — full stamp for created/updated
// timestamps stored as UTC. This is the go-to for anything the DB set
// automatically (audit fields, activity feed, etc.).
export function fmtInstantEastern(d) {
  if (d == null) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (!Number.isFinite(date.getTime())) return "";
  const datePart = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    month: "short", day: "numeric", year: "numeric"
  }).format(date);
  const timePart = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    hour: "numeric", minute: "2-digit"
  }).format(date);
  return `${datePart} · ${timePart} ET`;
}

// ── HH:MM (wall clock) formatters ─────────────────────────────────────

// Format an HH:MM string as its 12-hour equivalent. The stored value is
// ALREADY Eastern wall-clock (per the convention above), so no shift.
//
//   fmtMatchTime("14:30") → "2:30 PM"
//   fmtMatchTime("09:00") → "9:00 AM"
export function fmtMatchTime(hhmm) {
  if (!hhmm) return "";
  const m = String(hhmm).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return hhmm;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return hhmm;
  // Pin an arbitrary date so Intl formats 12-hour AM/PM correctly.
  const d = new Date(2026, 5, 1, h, min, 0);
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit"
  }).format(d);
}

// ── ET wall-clock → UTC instant (for countdown / scheduling math) ─────

// Convert an Eastern wall-clock (YYYY-MM-DD + HH:MM) to a UTC millisecond
// timestamp. DST-aware via a month heuristic — accurate everywhere except
// the DST transition weekends, where kick-off math may be off by an hour
// for a couple of days per year. Good enough for a community sports app;
// switch to a proper Intl-based conversion if precise DST accuracy on
// transition weekends ever matters.
//
//   EDT = UTC-4  (roughly March → early November)
//   EST = UTC-5  (roughly November → early March)
export function easternToMs(dateIso, timeHhmm) {
  if (!dateIso) return NaN;
  const t = timeHhmm && /\d{1,2}:\d{2}/.test(timeHhmm) ? timeHhmm : "12:00";
  const month = parseInt(dateIso.slice(5, 7), 10);
  const offset = (month >= 3 && month <= 10) ? "-04:00" : "-05:00";
  const ms = new Date(`${dateIso}T${t}:00${offset}`).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

// Backwards-compat alias — the OLD name (from the Central Time era).
// New code should call easternToMs directly. Behaviour is now identical
// to easternToMs; the "central" naming is just historical.
export const centralToMs = easternToMs;

// ── Small conveniences ────────────────────────────────────────────────

// Today's YYYY-MM-DD in Eastern Time (not the browser's timezone).
export function todayIsoEastern() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric", month: "2-digit", day: "2-digit"
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === "year")?.value;
  const m = parts.find(p => p.type === "month")?.value;
  const d = parts.find(p => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}
